import React, { useMemo, useState } from "react";
import {
  FaEnvelope,
  FaEnvelopeOpen,
  FaReply,
  FaTrash,
  FaUser,
} from "react-icons/fa";

import {
  cleanHtml,
  formatMessageDate,
  formatMessageTime,
  getMessagePreview,
  getUserName,
  getUserPhoto,
  DEFAULT_AVATAR,
  MESSAGE_TABS,
} from "../utils/messagerieUtils";

function MessageCard({
  message,
  activeTab,
  onToggleRead,
  onDelete,
  onReply,
  t,
}) {
  const [expanded, setExpanded] = useState(false);

  const isReceived = activeTab === MESSAGE_TABS.received;
  const contact = isReceived ? message.Sender : message.Recipient;

  const contactName = getUserName(
    contact,
    isReceived ? "Administrateur" : "Destinataire"
  );

  const photo = getUserPhoto(contact);
  const isUnread = isReceived && !message.read_message;

  const sanitizedContent = useMemo(
    () => cleanHtml(message.content || "Message vide"),
    [message.content]
  );

  const preview = useMemo(
    () => getMessagePreview(message.content, 150),
    [message.content]
  );

  return (
    <article
      className={`group overflow-hidden rounded-[1.7rem] border bg-white shadow-lg shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-2xl dark:bg-white/[0.05] dark:shadow-black/20 ${
        isUnread
          ? "border-sky-300 dark:border-sky-400/30"
          : "border-slate-200 dark:border-white/10"
      }`}
    >
      <div
        className={`h-1.5 ${
          isUnread
            ? "bg-gradient-to-r from-blue-600 to-orange-500"
            : "bg-slate-200 dark:bg-white/10"
        }`}
      />

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative">
              <img
                src={photo}
                alt={contactName}
                className="h-12 w-12 rounded-2xl object-cover"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_AVATAR;
                }}
              />

              <span
                className={`absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[10px] dark:border-slate-950 ${
                  isUnread
                    ? "bg-sky-600 text-white"
                    : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-white/60"
                }`}
              >
                {isUnread ? <FaEnvelope /> : <FaEnvelopeOpen />}
              </span>
            </div>

            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                {contactName}
              </p>

              <p className="mt-1 flex items-center gap-2 text-xs font-semibold text-slate-400">
                <span>{formatMessageDate(message.created_at)}</span>
                <span>·</span>
                <span>{formatMessageTime(message.created_at)}</span>
              </p>
            </div>
          </div>

          {isUnread && (
            <span className="shrink-0 rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
              Non lu
            </span>
          )}
        </div>

        <h3 className="mt-5 line-clamp-2 font-titre text-xl font-bold leading-tight text-slate-950 dark:text-white">
          {message.subject || "Sans sujet"}
        </h3>

        {expanded ? (
          <div
            className="prose prose-sm mt-4 max-w-none text-slate-600 prose-p:my-2 dark:prose-invert dark:text-white/60"
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
        ) : (
          <p className="mt-4 line-clamp-3 text-sm font-medium leading-7 text-slate-600 dark:text-white/55">
            {preview}
          </p>
        )}

        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-3 text-sm font-bold text-sky-700 transition hover:text-orange-600 dark:text-sky-200"
        >
          {expanded ? "Réduire" : "Lire le message"}
        </button>

        <div className="mt-5 flex flex-col gap-2 border-t border-slate-200 pt-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {isReceived && (
              <button
                type="button"
                onClick={() => onToggleRead(message)}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-white hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white"
              >
                {message.read_message ? <FaEnvelope /> : <FaEnvelopeOpen />}
                {message.read_message
                  ? t("messagerie.noReadMessage", {
                      defaultValue: "Marquer non lu",
                    })
                  : t("messagerie.readMessage", {
                      defaultValue: "Marquer lu",
                    })}
              </button>
            )}

            {isReceived && (
              <button
                type="button"
                onClick={() => onReply(message)}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 dark:bg-white dark:text-slate-950"
              >
                <FaReply />
                {t("messagerie.answer", { defaultValue: "Répondre" })}
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => onDelete(message)}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-xs font-bold text-red-700 transition hover:bg-red-100 dark:border-red-400/20 dark:bg-red-500/10 dark:text-red-200"
          >
            <FaTrash />
            {t("messagerie.delete", { defaultValue: "Supprimer" })}
          </button>
        </div>
      </div>
    </article>
  );
}

export default MessageCard;